import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, MapPin, Calendar, User, DollarSign, CheckCircle2, Circle } from "lucide-react";

export default function ProjectDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/projects/${id}`).then((r) => setData(r.data)); }, [id]);
  if (!data) return <div className="container-x py-16">Loading…</div>;
  const p = data.project;
  const milestones = [
    { label: "DPR Approved", done: p.progress >= 10 },
    { label: "Tender Awarded", done: p.progress >= 25 },
    { label: "Site Handover", done: p.progress >= 40 },
    { label: "Construction 50%", done: p.progress >= 55 },
    { label: "Commissioning", done: p.progress >= 85 },
    { label: "COD Achieved", done: p.progress >= 100 },
  ];
  return (
    <div className="container-x py-10">
      <Link to="/projects" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4"/>All projects</Link>
      <div className="mt-4 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-6">
          <div className="label-tiny text-[hsl(var(--primary))]">{p.project_type.replace("_"," ")}</div>
          <h1 className="mt-1 font-heading font-bold text-3xl">{p.name}</h1>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <Info label="District" icon={MapPin} value={p.district}/>
            <Info label="Contractor" icon={User} value={p.contractor}/>
            <Info label="Budget" icon={DollarSign} value={`₹${(p.budget/1e5).toFixed(1)} Lakh`}/>
            <Info label="Timeline" icon={Calendar} value={`${p.start_date} → ${p.end_date}`}/>
          </div>
          <div className="mt-6">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-muted-foreground">Overall Progress</span>
              <span className="font-semibold">{p.progress}%</span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-[hsl(var(--primary))]" style={{ width: `${p.progress}%`}}/>
            </div>
          </div>
          <div className="mt-8">
            <div className="label-tiny mb-3">Milestones</div>
            <div className="space-y-3">
              {milestones.map((m,i)=>(
                <div key={i} className="flex items-center gap-3">
                  {m.done ? <CheckCircle2 className="h-5 w-5 text-[hsl(var(--primary))]"/> : <Circle className="h-5 w-5 text-muted-foreground"/>}
                  <div className={`text-sm ${m.done ? "font-medium" : "text-muted-foreground"}`}>{m.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="label-tiny">Health Score</div>
          <div className="mt-2 font-heading font-bold text-4xl">{Math.max(50, Math.min(100, 60 + p.progress/3)).toFixed(0)}<span className="text-muted-foreground text-xl">/100</span></div>
          <div className="mt-1 text-xs text-muted-foreground">Composite of schedule, budget & quality</div>
          <div className="mt-6 grid gap-2 text-sm">
            <Row k="Schedule" v="On track"/>
            <Row k="Budget"   v="Within ±3%"/>
            <Row k="Quality"  v="A grade"/>
            <Row k="Safety"   v="0 LTI"/>
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ label, icon: Icon, value }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground flex items-center gap-1"><Icon className="h-3 w-3"/>{label}</div>
      <div className="mt-1 font-semibold">{value ?? "—"}</div>
    </div>
  );
}
function Row({ k, v }) {
  return <div className="flex items-center justify-between"><span className="text-muted-foreground">{k}</span><span className="font-medium">{v}</span></div>;
}
